

// ==================== FILE: src/components/GDPRBanner/index.js (NO LOCATION REQUEST) ====================
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './index.css';

const GDPRBanner = ({ config, existingLocation }) => {
    const navigate = useNavigate();
    const [showBanner, setShowBanner] = useState(false);
    const [showRejectionMessage, setShowRejectionMessage] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const consentStatus = localStorage.getItem('gdpr_consent');
        const consentTimestamp = localStorage.getItem('gdpr_consent_timestamp');

        if (!consentStatus) {
            setShowBanner(true);
        } else if (consentStatus === 'rejected') {
            setShowRejectionMessage(true);
        }

        // Re-ask for consent after 6 months
        if (consentTimestamp) {
            const sixMonthsInMs = 6 * 30 * 24 * 60 * 60 * 1000;
            const timeSinceConsent = Date.now() - parseInt(consentTimestamp);

            if (timeSinceConsent > sixMonthsInMs) {
                localStorage.removeItem('gdpr_consent');
                localStorage.removeItem('gdpr_consent_timestamp');
                setShowBanner(true);
                setShowRejectionMessage(false);
            }
        }
    }, []);

    // Generate unique session ID
    const generateSessionId = () => {
        return 'anon_' + Date.now() + '_' + Math.random().toString(36).substr(2, 16);
    };

    // ⭐ Get location from localStorage (already requested on app load)
    const getStoredLocation = () => {
        const savedLocation = localStorage.getItem('user_location');
        const locationStatus = localStorage.getItem('location_requested');

        if (savedLocation) {
            try {
                return JSON.parse(savedLocation);
            } catch (e) {
                console.error('Error parsing saved location:', e);
                return null;
            }
        }

        return {
            status: locationStatus || 'not_requested'
        };
    };

    const handleAccept = () => {
        const sessionId = localStorage.getItem('session_id') || generateSessionId();
        localStorage.setItem('session_id', sessionId);
        
        localStorage.setItem('gdpr_consent', 'accepted');
        localStorage.setItem('gdpr_consent_timestamp', Date.now().toString());
        
        setShowBanner(false);
        setShowRejectionMessage(false);
        enableTracking();
    };

    const handleAcceptAfterReject = () => {
        localStorage.setItem('gdpr_consent', 'accepted');
        localStorage.setItem('gdpr_consent_timestamp', Date.now().toString());
        
        setShowBanner(false);
        setShowRejectionMessage(false);
        enableTracking();
    };

    const handleReject = () => {
        const sessionId = localStorage.getItem('session_id') || generateSessionId();
        localStorage.setItem('session_id', sessionId);
        
        localStorage.setItem('gdpr_consent', 'rejected');
        localStorage.setItem('gdpr_consent_timestamp', Date.now().toString());
        
        setShowBanner(false);
        setShowRejectionMessage(true);
        disableTracking();
    };

    const enableTracking = () => {
        console.log('✅ Tracking enabled');
        // Enable Google Analytics, Facebook Pixel, etc.
    };

    const disableTracking = () => {
        console.log('❌ Tracking disabled');
        // Disable all tracking scripts
    };

    const handlePrivacyPolicyClick = (e) => {
        e.preventDefault();
        setShowRejectionMessage(false);
        navigate('/privacy-policy');
    };

    const handleCloseTab = () => {
        setShowRejectionMessage(false);
    };

    if (showRejectionMessage) {
        return (
            <div className="gdpr-rejection-overlay">
                <div className="gdpr-rejection-container">
                    <div className="rejection-icon">
                        <i className="fa fa-shield" aria-hidden="true"></i>
                    </div>
                    <h2>We Respect Your Privacy Choice</h2>
                    <p>
                        Thank you for visiting {config.name}. We understand and respect your
                        decision regarding data consent. However, to provide you with the best
                        experience and functionality, including online reservations, personalized
                        recommendations, and special offers, we need your consent to process
                        certain information.
                    </p>
                    <p>
                        Without this consent, we're unable to offer our full range of services.
                        We encourage you to review our{' '}
                        <a
                            href="/privacy-policy"
                            onClick={handlePrivacyPolicyClick}
                            className="gdpr-link"
                        >
                            Privacy Policy
                        </a>{' '}
                        to understand how we protect and handle your data.
                    </p>
                    <div className="rejection-actions">
                        <button
                            onClick={handleAcceptAfterReject}
                            className="btn-accept-alt"
                            disabled={isSubmitting}
                            type="button"
                        >
                            {isSubmitting ? 'Processing...' : 'I Changed My Mind - Accept'}
                        </button>
                        <button
                            onClick={handleCloseTab}
                            className="btn-close-tab"
                            type="button"
                        >
                            Close Tab
                        </button>
                    </div>
                    <p className="rejection-note">
                        You can also visit us in person at: <strong>{config.footer.contact.address}</strong>
                    </p>
                </div>
            </div>
        );
    }

    if (!showBanner) {
        return null;
    }

    return (
        <div className="gdpr-banner">
            <div className="gdpr-content">
                <div className="gdpr-icon">
                    <i className="fa fa-cookie-bite" aria-hidden="true"></i>
                </div>
                <div className="gdpr-text">
                    <h3>We Value Your Privacy</h3>
                    <p>
                        We use cookies and similar technologies to enhance your browsing experience,
                        personalize content, analyze traffic, and provide social media features.
                        {existingLocation && existingLocation.latitude && (
                            <strong> We've detected your location to provide better local services.</strong>
                        )}
                        {' '}By clicking "Accept All", you consent to our use of cookies and data processing.
                        You can manage your preferences or learn more in our{' '}
                        <Link to="/privacy-policy" className="gdpr-link">Privacy Policy</Link>.
                    </p>
                </div>
                <div className="gdpr-actions">
                    <button
                        onClick={handleAccept}
                        className="btn-accept"
                        disabled={isSubmitting}
                        type="button"
                    >
                        {isSubmitting ? 'Processing...' : 'Accept All'}
                    </button>
                    <button
                        onClick={handleReject}
                        className="btn-reject"
                        disabled={isSubmitting}
                        type="button"
                    >
                        {isSubmitting ? 'Processing...' : 'Reject'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GDPRBanner;